import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateStatus, translatePartnerType } from '../i18n/translations';
import { hasExpress } from '../utils/express';
import { getImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Package, MapPin, Archive as ArchiveIcon, ChevronDown, ChevronRight, ImageIcon, Globe, Bell, Truck, CheckCircle, LayoutDashboard, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { TrackingDialog } from '../components/TrackingDialog';
import { InvoicesPanel } from '../components/InvoicesPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

export const TransportDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Dialogs
  const [addressOrder, setAddressOrder] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingLink, setTrackingLink] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageOrderId, setPackageOrderId] = useState(null);
  const [packageWeight, setPackageWeight] = useState('');
  const [packagePrice, setPackagePrice] = useState('');
  const [selectedForColis, setSelectedForColis] = useState({});
  const [colisDialogOpen, setColisDialogOpen] = useState(false);
  const [colisWeight, setColisWeight] = useState('');
  const [colisPrice, setColisPrice] = useState('');
  const [transportPackages, setTransportPackages] = useState([]);
  const [transportInvoices, setTransportInvoices] = useState([]);
  const [uninvoicedItems, setUninvoicedItems] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedColis, setExpandedColis] = useState({});
  const [colisPackageDialog, setColisPackageDialog] = useState(null);
  const [colisPackageWeight, setColisPackageWeight] = useState('');
  const [colisPackagePrice, setColisPackagePrice] = useState('');

  // My Info
  const [myInfo, setMyInfo] = useState(null);
  const [myInfoOpen, setMyInfoOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  // Notifications
  const [notifBanner, setNotifBanner] = useState(null);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const prevOrderCount = useRef(null);
  const prevOrderStatuses = useRef(null);

  useEffect(() => {
    if (user?.language && user.language !== language) setLanguage(user.language);
    fetchMyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const fetchMyInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/partner/my-info`, { withCredentials: true });
      setMyInfo(res.data);
    } catch (e) { console.error(e); }
  };

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm_password) { toast.error(t('confirm_password')); return; }
    if (pwdForm.new_password.length < 4) { toast.error('Mot de passe trop court'); return; }
    setPwdLoading(true);
    try {
      await axios.put(`${API}/api/partner/change-password`, { current_password: pwdForm.current_password, new_password: pwdForm.new_password }, { withCredentials: true });
      toast.success(t('password_changed'));
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (err) { toast.error(err.response?.data?.detail || t('password_error')); }
    finally { setPwdLoading(false); }
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') { toast.success(t('notifications_enabled')); new Notification('Panther', { body: t('notifications_enabled'), icon: LOGO_URL }); }
    else { toast.error(t('notifications_denied')); }
  };

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 800; osc.type = 'sine'; gain.gain.value = 0.3;
      osc.start(); osc.stop(ctx.currentTime + 0.3);
    } catch (e) { /* Audio not available */ }
  }, []);

  const fetchOrders = useCallback(async (isPolling = false) => {
    try {
      const [ordersRes, archivesRes, pkgRes, invRes, uninvRes] = await Promise.all([
        axios.get(`${API}/api/partner/orders`, { withCredentials: true }),
        axios.get(`${API}/api/partner/archives`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport/packages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport/invoices`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport/uninvoiced-items`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const newOrders = ordersRes.data;

      if (isPolling && prevOrderCount.current !== null && newOrders.length > prevOrderCount.current) {
        const diff = newOrders.length - prevOrderCount.current;
        const msg = diff === 1 ? t('new_order_notification') : `${diff} ${t('new_orders_notification')}`;
        setNotifBanner(msg);
        playNotifSound();
        setTimeout(() => setNotifBanner(null), 8000);
      }
      if (isPolling && prevOrderStatuses.current) {
        for (const o of newOrders) {
          if (prevOrderStatuses.current[o.id] && prevOrderStatuses.current[o.id] !== o.status) {
            setNotifBanner(t('status_update_notification'));
            playNotifSound();
            setTimeout(() => setNotifBanner(null), 8000);
            break;
          }
        }
      }

      prevOrderCount.current = newOrders.length;
      const statusMap = {};
      newOrders.forEach(o => { statusMap[o.id] = o.status; });
      prevOrderStatuses.current = statusMap;

      setOrders(newOrders);
      setArchives(archivesRes.data);
      setTransportPackages(pkgRes.data || []);
      setTransportInvoices(invRes.data || []);
      setUninvoicedItems(uninvRes.data || []);
    } catch (e) { console.error('Fetch error:', e); }
    finally { setLoading(false); }
  }, [t, playNotifSound]);

  useEffect(() => {
    fetchOrders(false);
    const interval = setInterval(() => fetchOrders(true), 300000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/api/partner/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`${t('status')}: ${translateStatus(newStatus, t)}`);
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openTrackingDialog = (order) => {
    setTrackingOrder(order);
    setTrackingLink(order.tracking_link || '');
    setTrackingNumber(order.tracking_number || '');
    setTrackingDialogOpen(true);
  };

  const handleSaveTracking = async () => {
    if (trackingLink && trackingLink.trim() && !trackingLink.trim().startsWith('http://') && !trackingLink.trim().startsWith('https://')) {
      toast.error('Le lien tracking doit commencer par http:// ou https://');
      return;
    }
    try {
      if (trackingOrder?._isPackage) {
        await axios.put(`${API}/api/transport/packages/${trackingOrder.id}/tracking`, { tracking_link: trackingLink, tracking_number: trackingNumber }, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/orders/${trackingOrder.id}/tracking`, { tracking_link: trackingLink, tracking_number: trackingNumber }, { withCredentials: true });
      }
      toast.success('Tracking mis à jour');
      setTrackingDialogOpen(false);
      fetchOrders(false);
    } catch { toast.error('Erreur'); }
  };

  const openPackageDialog = (orderId) => {
    setPackageOrderId(orderId);
    setPackageWeight('');
    setPackagePrice('');
    setPackageDialogOpen(true);
  };

  const handleValidatePackage = async () => {
    if (!packageWeight || !packagePrice) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.put(`${API}/api/transport-orders/${packageOrderId}/validate-package`, {
        package_weight: parseFloat(packageWeight), package_price_vnd: parseFloat(packagePrice)
      }, { withCredentials: true });
      toast.success(t('package_validated'));
      setPackageDialogOpen(false);
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const selectedColisIds = Object.keys(selectedForColis).filter(k => selectedForColis[k]);

  const handleValidateColisPackage = async () => {
    if (!colisPackageDialog || !colisPackageWeight || !colisPackagePrice) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.put(`${API}/api/transport/packages/${colisPackageDialog}/validate`, {
        weight: parseFloat(colisPackageWeight), price: parseFloat(colisPackagePrice)
      }, { withCredentials: true });
      toast.success(t('package_validated'));
      setColisPackageDialog(null);
      setColisPackageWeight('');
      setColisPackagePrice('');
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openColisDialog = () => {
    if (selectedColisIds.length < 1) { toast.error(t('select_at_least_2')); return; }
    setColisWeight(''); setColisPrice('');
    setColisDialogOpen(true);
  };

  const handleValidateColis = async () => {
    if (!colisWeight || !colisPrice) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.post(`${API}/api/transport/packages`, {
        order_ids: selectedColisIds, weight: parseFloat(colisWeight), price_vnd: parseFloat(colisPrice)
      }, { withCredentials: true });
      toast.success(`${t('package_validated')} (${selectedColisIds.length} ${t('colis_orders_count')})`);
      setColisDialogOpen(false);
      setSelectedForColis({});
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateInvoice = async (items) => {
    if (!items || !items.length) { toast.error('Sélectionnez au moins un élément'); return; }
    try {
      const res = await axios.post(`${API}/api/transport/invoices`, { items }, { withCredentials: true });
      toast.success(`Facture N°${res.data.invoice_number} créée`);
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleIgnoreItem = async (itemId, itemType) => {
    if (!window.confirm(t('ignore') + ' ?')) return;
    try {
      await axios.put(`${API}/api/transport/ignore-item`, { item_id: itemId, item_type: itemType }, { withCredentials: true });
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadInvoicePdf = async (invoiceId, invoiceNum) => {
    try {
      const res = await axios.get(`${API}/api/transport/invoices/${invoiceId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Facture_Transport_${invoiceNum}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error('Erreur téléchargement PDF'); }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try { await axios.put(`${API}/api/auth/language`, { language: lang }, { withCredentials: true }); } catch (e) { /* */ }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const getNextStatus = (orderStatus) => {
    const flow = ['en cours de livraison client', 'livrée'];
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const idx = flow.findIndex(s => norm(s) === norm(orderStatus));
    if (idx >= 0 && idx < flow.length - 1) return flow[idx + 1];
    return null;
  };

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const toggleMonth = (label) => setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));

  const sidebarItems = [
    { id: 'dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { id: 'archives', label: t('archives_title'), icon: ArchiveIcon },
    { id: 'invoices', label: t('invoices_title'), icon: FileText },
  ];

  const DeliveryCell = ({ order }) => {
    if (order.tracking_delivered_date) {
      return (
        <span className="text-xs font-bold text-emerald-700">
          {t('delivered_short')} {new Date(order.tracking_delivered_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      );
    }
    if (order.tracking_first_event_date) {
      const est = new Date(new Date(order.tracking_first_event_date).getTime() + 15 * 24 * 60 * 60 * 1000);
      const overdue = est.toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10);
      return (
        <span className={`text-xs font-bold ${overdue ? 'text-red-600 animate-pulse' : 'text-emerald-600'}`}>
          {t('estimated_short')} {est.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 relative z-10">
        <div className="flex items-center justify-between max-w-full mx-auto">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="partner-logo" />
            <div>
              <p className="text-sm text-slate-600">{user?.name} - {translatePartnerType('transport', t)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {notifPermission !== 'granted' ? (
              <Button data-testid="enable-notifications-btn" variant="outline" size="sm" onClick={requestNotifPermission} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <Bell size={14} className="mr-1" /> {t('enable_notifications')}
              </Button>
            ) : (
              <Badge data-testid="notifications-enabled-badge" className="bg-emerald-100 text-emerald-700 border border-emerald-200 h-8 px-3">
                <Bell size={14} className="mr-1" /> {t('notifications_enabled')}
              </Badge>
            )}
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="partner-lang-switcher" className="w-28 h-8 text-xs border-slate-200">
                <Globe size={14} className="mr-1" />
                <SelectValue>{LANG_LABELS[language]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="vi">Tiếng Việt</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="my-info-button" variant="outline" size="sm" onClick={() => setMyInfoOpen(true)}>
              {t('my_information')}
            </Button>
            <Button data-testid="partner-logout-button" onClick={handleLogout} variant="ghost" className="text-slate-600">
              <LogOut size={18} className="mr-2" /> {t('logout')}
            </Button>
          </div>
        </div>
        {notifBanner && (
          <div data-testid="new-order-notification" className="absolute top-full left-0 right-0 z-50 animate-in slide-in-from-top">
            <div className="max-w-7xl mx-auto px-6 py-3">
              <div className="bg-red-600 text-white rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
                <Bell size={20} className="animate-bounce" />
                <span className="font-semibold">{notifBanner}</span>
                <button onClick={() => setNotifBanner(null)} className="ml-auto text-red-200 hover:text-white text-lg font-bold">&times;</button>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="flex flex-1">
        {/* LEFT SIDEBAR */}
        <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0" data-testid="transport-sidebar">
          <nav className="p-3 space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`transport-tab-${item.id}`}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                  {item.id === 'dashboard' && orders.length > 0 && (
                    <span className={`ml-auto text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1 ${isActive ? 'bg-white text-slate-900' : 'bg-slate-200 text-slate-700'}`}>
                      {orders.length}
                    </span>
                  )}
                  {item.id === 'invoices' && transportInvoices.filter(i => !i.paid).length > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                      {transportInvoices.filter(i => !i.paid).length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 p-6 space-y-6 overflow-auto">
          {/* ===== DASHBOARD TAB ===== */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <Card data-testid="partner-orders-card" className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-medium">
                      {orders.length} {t('pending_orders')}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                    </div>
                  ) : orders.length === 0 ? (
                    <p className="text-center py-8 text-sm text-slate-500">{t('no_pending_orders')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-14"></TableHead>
                            <TableHead className="font-medium">{t('order_number')}</TableHead>
                            <TableHead className="font-medium">{t('address')}</TableHead>
                            <TableHead className="font-medium">{t('status')}</TableHead>
                            <TableHead className="font-medium">{t('add_tracking')}</TableHead>
                            <TableHead className="font-medium">{t('weight_kg')} / {t('price_vnd')}</TableHead>
                            <TableHead className="font-medium">{t('delivery_col')}</TableHead>
                            <TableHead className="font-medium">{t('actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            // Group orders: ungrouped first, then grouped by colis
                            const ungrouped = [];
                            const colisGroups = {};
                            const sorted = [...orders].sort((a, b) => (a.tracking_number ? 1 : 0) - (b.tracking_number ? 1 : 0));
                            sorted.forEach(order => {
                              if (order.transport_package_id) {
                                if (!colisGroups[order.transport_package_id]) colisGroups[order.transport_package_id] = [];
                                colisGroups[order.transport_package_id].push(order);
                              } else {
                                ungrouped.push(order);
                              }
                            });
                            // Build colis index map from packages
                            const colisIndex = {};
                            transportPackages.forEach((pkg, i) => { colisIndex[pkg._id] = i + 1; });
                            const rows = [];

                            // Render ungrouped orders
                            ungrouped.forEach(order => rows.push({ type: 'order', order }));

                            // Render grouped orders with colis header
                            Object.entries(colisGroups).forEach(([pkgId, groupOrders]) => {
                              const pkg = transportPackages.find(p => p._id === pkgId);
                              const colisNum = colisIndex[pkgId] || '?';
                              rows.push({ type: 'colis-header', pkgId, colisNum, pkg, count: groupOrders.length });
                              groupOrders.forEach(order => rows.push({ type: 'colis-child', order, pkgId, inColis: true }));
                            });

                            return rows.map((row, idx) => {
                              if (row.type === 'colis-header') {
                                const isExpanded = expandedColis[row.pkgId];
                                const colisName = row.pkg?.name || `${t('colis_header')} #${row.colisNum}`;
                                const hasWeight = row.pkg?.weight_kg > 0;
                                return (
                                  <TableRow key={`colis-${row.pkgId}`} className="bg-blue-50 border-l-4 border-l-blue-500">
                                    <TableCell className="py-2">
                                      <button onClick={() => setExpandedColis(prev => ({ ...prev, [row.pkgId]: !prev[row.pkgId] }))}>
                                        {isExpanded ? <ChevronDown size={16} className="text-blue-600" /> : <ChevronRight size={16} className="text-blue-600" />}
                                      </button>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpandedColis(prev => ({ ...prev, [row.pkgId]: !prev[row.pkgId] }))}>
                                        <Package size={16} className="text-blue-600" />
                                        <span className="font-bold text-blue-800 text-sm">{colisName}</span>
                                        <span className="text-xs text-blue-600">({row.count} {t('colis_orders_count')})</span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Button size="sm" variant="outline" className="text-xs uppercase"
                                        onClick={(e) => { e.stopPropagation(); const fo = colisGroups[row.pkgId]?.[0]; if (fo) { setAddressOrder(fo); setAddressDialogOpen(true); } }}>
                                        <MapPin size={14} className="mr-1" /> {t('address')}
                                      </Button>
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {row.pkg?.tracking_number?.trim() ? (
                                          <>
                                            <span className="text-xs font-medium text-slate-700">{t('package')}: {row.pkg.tracking_number}</span>
                                            <Button size="sm" className="h-5 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                              onClick={(e) => { e.stopPropagation(); window.open(`${window.location.origin}/track?n=${row.pkg.tracking_number}`, '_blank'); }}>
                                              <Package size={10} /> {t('suivi')}
                                            </Button>
                                          </>
                                        ) : (
                                          <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={(e) => { e.stopPropagation(); setTrackingOrder({ id: row.pkgId, order_number: colisName, _isPackage: true }); setTrackingLink(row.pkg?.tracking_link || ''); setTrackingNumber(row.pkg?.tracking_number || ''); setTrackingDialogOpen(true); }}>
                                            <Truck size={12} /> {t('add_tracking')}
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {hasWeight ? (
                                        <div className="text-xs space-y-0.5">
                                          <div className="font-bold text-emerald-700">{row.pkg.weight_kg} kg</div>
                                          <div className="font-bold">{row.pkg.price_vnd?.toLocaleString()} VND</div>
                                        </div>
                                      ) : (
                                        <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                          onClick={(e) => { e.stopPropagation(); setColisPackageDialog(row.pkgId); setColisPackageWeight(''); setColisPackagePrice(''); }}>
                                          <Package size={12} /> {t('add_weight')}
                                        </Button>
                                      )}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                  </TableRow>
                                );
                              }
                              // Hide colis children when collapsed
                              if (row.type === 'colis-child' && !expandedColis[row.pkgId]) return null;

                              const order = row.order;
                              return (
                            <TableRow key={order.id} data-testid="partner-order-row" className={`hover:bg-slate-100 transition-colors ${row.inColis ? 'bg-blue-50/30' : ''}`}>
                              <TableCell>
                                {order.images?.length > 0 ? (
                                  <img src={getImageUrl(order.images[0])} alt="" data-testid="order-thumbnail"
                                    className="w-10 h-10 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80"
                                    onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }} />
                                ) : (
                                  <div className="w-10 h-10 bg-slate-200 rounded border border-slate-300 flex items-center justify-center">
                                    <ImageIcon size={14} className="text-slate-400" />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {order.order_number}
                                {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                              </TableCell>
                              <TableCell>
                                <Button data-testid="partner-address-button" size="sm" variant="outline" className="text-xs uppercase"
                                  onClick={() => { setAddressOrder(order); setAddressDialogOpen(true); }}>
                                  <MapPin size={14} className="mr-1" /> {t('address')}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs uppercase">
                                  {translateStatus(order.status, t)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {!order.tracking_number?.trim() && !order.tracking_link?.trim() && (
                                    <Button data-testid="add-tracking-btn" size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openTrackingDialog(order)}>
                                      <Truck size={12} /> {t('add_tracking')}
                                    </Button>
                                  )}
                                  {order.tracking_number && (
                                    <span className="text-xs font-medium text-slate-700">{t('package')}: {order.tracking_number}</span>
                                  )}
                                  {order.tracking_number && (
                                    <Button data-testid="tracking-detail-btn" size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={() => window.open(`${window.location.origin}/track?n=${order.tracking_number}`, '_blank')}>
                                      <Package size={12} /> SUIVI
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {row.inColis ? (
                                  <span className="text-xs text-slate-400">—</span>
                                ) : order.transport_validated ? (
                                  <div className="text-xs space-y-0.5">
                                    <div className="font-bold text-emerald-700">{order.package_weight} kg</div>
                                    <div className="font-bold">{(order.package_price_vnd || 0).toLocaleString()} VND</div>
                                  </div>
                                ) : (
                                  <Button data-testid="add-weight-btn" size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openPackageDialog(order.id)}>
                                    <Package size={12} /> {t('add_weight')}
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell><DeliveryCell order={order} /></TableCell>
                              <TableCell>
                                {(() => {
                                  const ns = getNextStatus(order.status);
                                  return ns ? (
                                    <Button data-testid="partner-change-status-button" size="sm" className="bg-slate-900 hover:bg-slate-800 text-xs uppercase"
                                      onClick={() => handleStatusChange(order.id, ns)}>
                                      {translateStatus(ns, t)}
                                    </Button>
                                  ) : null;
                                })()}
                              </TableCell>
                            </TableRow>
                              );
                            });
                          })()}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===== ARCHIVES TAB ===== */}
          {activeTab === 'archives' && (
            <Card data-testid="partner-archives-card" className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ArchiveIcon size={20} className="text-slate-600" />
                  <CardTitle className="text-lg font-medium">{t('partner_archives')}</CardTitle>
                </div>
                <p className="text-xs text-slate-500 mt-1">{archives.length} {t('partner_archives_subtitle')}</p>
              </CardHeader>
              <CardContent>
                {archives.length === 0 ? (
                  <p className="text-center py-8 text-sm text-slate-500">Aucune archive</p>
                ) : (
                  <div className="space-y-3">
                    {groupByMonth(archives).map(group => (
                      <div key={group.label} data-testid="partner-archive-month-group" className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          data-testid="toggle-partner-archive-month"
                          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                          onClick={() => toggleMonth(group.label)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                            <span className="font-semibold text-slate-900">{group.label}</span>
                          </div>
                          <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                        </button>
                        {expandedMonths[group.label] && (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-white">
                                  <TableHead className="font-medium w-14"></TableHead>
                                  <TableHead className="font-medium">{t('order_number')}</TableHead>
                                  <TableHead className="font-medium">{t('order_date')}</TableHead>
                                  <TableHead className="font-medium">{t('status')}</TableHead>
                                  <TableHead className="font-medium">{t('weight_kg')} / {t('price_vnd')}</TableHead>
                                  <TableHead className="font-medium">{t('delivery_col')}</TableHead>
                                  <TableHead className="font-medium">{t('suivi')}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.orders.map(order => (
                                  <TableRow key={order.id} data-testid="partner-archive-row" className="hover:bg-slate-50">
                                    <TableCell>
                                      {order.images?.length > 0 ? (
                                        <img src={getImageUrl(order.images[0])} alt=""
                                          className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80"
                                          onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }} />
                                      ) : (
                                        <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                          <ImageIcon size={14} className="text-slate-400" />
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {order.order_number}
                                      {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                    </TableCell>
                                    <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                                    <TableCell>
                                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{translateStatus(order.status, t)}</Badge>
                                    </TableCell>
                                    <TableCell>
                                      <div className="text-xs">
                                        {order.package_weight ? <div className="font-bold text-emerald-700">{order.package_weight} kg</div> : null}
                                        {order.package_price_vnd ? <div className="font-bold">{order.package_price_vnd.toLocaleString()} VND</div> : null}
                                        {!order.package_weight && !order.package_price_vnd && <span className="text-slate-400">—</span>}
                                      </div>
                                    </TableCell>
                                    <TableCell><DeliveryCell order={order} /></TableCell>
                                    <TableCell>
                                      {order.tracking_number && (
                                        <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                          onClick={() => window.open(`${window.location.origin}/track?n=${order.tracking_number}`, '_blank')}>
                                          <Package size={12} /> SUIVI
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===== FACTURES TAB ===== */}
          {activeTab === 'invoices' && (
            <InvoicesPanel
              uninvoicedItems={uninvoicedItems}
              invoices={transportInvoices}
              onCreateInvoice={handleCreateInvoice}
              onDownloadPdf={handleDownloadInvoicePdf}
              onIgnoreItem={handleIgnoreItem}
              canDelete={false}
              canPay={false}
              canIgnore={true}
              t={t}
            />
          )}
        </main>
      </div>

      {/* DIALOGS */}

      {/* Colis Package Weight/Price Dialog */}
      <Dialog open={!!colisPackageDialog} onOpenChange={() => setColisPackageDialog(null)}>
        <DialogContent data-testid="colis-package-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package size={18} /> {t('validate_package')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">{t('weight_kg')}</Label>
              <Input data-testid="colis-pkg-weight-input" type="number" step="0.1" min="0" value={colisPackageWeight} onChange={e => setColisPackageWeight(e.target.value)} placeholder="Ex: 12.5" />
            </div>
            <div>
              <Label className="text-sm font-semibold">{t('price_vnd')}</Label>
              <Input data-testid="colis-pkg-price-input" type="number" step="1000" min="0" value={colisPackagePrice} onChange={e => setColisPackagePrice(e.target.value)} placeholder="Ex: 500000" />
            </div>
            <p className="text-xs text-red-500 font-medium">{t('transport_validate_warning')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setColisPackageDialog(null)}>{t('cancel')}</Button>
              <Button data-testid="colis-pkg-validate-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleValidateColisPackage}>
                <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent data-testid="address-dialog">
          <DialogHeader>
            <DialogTitle>{t('address_delivery')} - {addressOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {addressOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">{t('name')}</Label>
                  <p className="font-medium text-lg">{addressOrder.client_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t('firstname')}</Label>
                  <p className="font-medium text-lg">{addressOrder.client_firstname || '-'}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t('address')}</Label>
                <p className="font-medium">{addressOrder.client_address || t('not_provided')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">{t('postal_code')}</Label>
                  <p className="font-medium" data-testid="address-postal-code">{addressOrder.client_postal_code || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t('city')}</Label>
                  <p className="font-medium" data-testid="address-city">{addressOrder.client_city || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500">{t('country')}</Label>
                  <p className="font-medium">{addressOrder.client_country}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t('phone')}</Label>
                  <p className="font-medium">{addressOrder.client_phone || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
        <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
          <DialogHeader>
            <DialogTitle>{t('image_popup_title')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {imagePopupImages.map((img, i) => (
              <img key={`popup-${img}`} src={getImageUrl(img)} alt="" className="w-full rounded-lg border border-slate-200" />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
        <DialogContent className="max-w-md" data-testid="tracking-dialog">
          <DialogHeader>
            <DialogTitle>Tracking — {trackingOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">{t('tracking_link_label')}</Label>
              <Input data-testid="tracking-link-input" value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="https://..." className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">{t('tracking_number_label')}</Label>
              <Input data-testid="tracking-number-input" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Ex: FR123456789" className="mt-1" />
            </div>
            <Button data-testid="save-tracking-btn" className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleSaveTracking}>{t('save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
        <DialogContent data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle>{t('validate_package')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">{t('weight_kg')}</Label>
              <Input data-testid="package-weight-input" type="number" step="0.1" min="0" value={packageWeight} onChange={e => setPackageWeight(e.target.value)} placeholder="Ex: 12.5" />
            </div>
            <div>
              <Label className="text-sm font-semibold">{t('price_vnd')}</Label>
              <Input data-testid="package-price-input" type="number" step="1000" min="0" value={packagePrice} onChange={e => setPackagePrice(e.target.value)} placeholder="Ex: 500000" />
            </div>
            <p className="text-xs text-red-500 font-medium">{t('transport_validate_warning')}</p>
            <div className="flex justify-end gap-2">
              <Button data-testid="package-cancel-btn" variant="outline" onClick={() => setPackageDialogOpen(false)}>{t('cancel')}</Button>
              <Button data-testid="package-validate-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleValidatePackage}>
                <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={colisDialogOpen} onOpenChange={setColisDialogOpen}>
        <DialogContent data-testid="colis-dialog">
          <DialogHeader>
            <DialogTitle>{t('create_colis')} ({selectedColisIds.length} {t('colis_orders_count')})</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">{t('weight_kg')} total</Label>
                <Input type="number" step="0.1" min="0" value={colisWeight} onChange={e => setColisWeight(e.target.value)} placeholder="Poids total" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t('price_vnd')} total</Label>
                <Input type="number" step="1000" min="0" value={colisPrice} onChange={e => setColisPrice(e.target.value)} placeholder="Prix total" />
              </div>
            </div>
            <p className="text-xs text-red-500 font-medium">{t('transport_validate_warning')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setColisDialogOpen(false)}>{t('cancel')}</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleValidateColis}>
                <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Info Dialog */}
      <Dialog open={myInfoOpen} onOpenChange={setMyInfoOpen}>
        <DialogContent className="max-w-md" data-testid="my-info-dialog">
          <DialogHeader>
            <DialogTitle>{t('my_information')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <p data-testid="my-info-email" className="text-sm font-medium">{myInfo?.email || user?.email}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t('password')}</Label>
                <p className="text-sm font-medium">********</p>
              </div>
            </div>
            {!showChangePassword ? (
              <Button data-testid="change-password-btn" variant="outline" size="sm" onClick={() => setShowChangePassword(true)} className="w-full">
                {t('change_password')}
              </Button>
            ) : (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('current_password')}</Label>
                    <Input data-testid="current-password-input" type="password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('new_password')}</Label>
                    <Input data-testid="new-password-input" type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('confirm_password')}</Label>
                    <Input data-testid="confirm-password-input" type="password" value={pwdForm.confirm_password} onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="submit-password-change" size="sm" onClick={handleChangePassword} disabled={pwdLoading} className="flex-1 bg-slate-900 hover:bg-slate-800">
                    {pwdLoading ? '...' : t('save')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowChangePassword(false); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }}>{t('cancel')}</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
